import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, authenticateAnonymously } from '../firebase';
import { TEAMS } from '../types';

interface PreloadedTeamData {
  [teamId: string]: {
    leagueName: string;
    scheduleData: any[];
    standingsData: any[];
    loading: boolean;
    error: string | null;
  };
}

export const usePreloadTeamData = () => {
  const [allTeamData, setAllTeamData] = useState<PreloadedTeamData>({});
  const [isPreloading, setIsPreloading] = useState(true);

  useEffect(() => {
    const unsubscribers: Array<() => void> = [];

    const preloadAllTeams = async () => {
      // Erst authentifizieren
      const authSuccess = await authenticateAnonymously();
      if (!authSuccess) {
        setIsPreloading(false);
        return;
      }

      // Für jedes Team Daten laden
      TEAMS.forEach((team) => {
        // Initialize team data
        setAllTeamData(prev => ({
          ...prev,
          [team.id]: {
            leagueName: '',
            scheduleData: [],
            standingsData: [],
            loading: true,
            error: null
          }
        }));

        // Schedule data listener
        const scheduleRef = doc(db, 'schedule', team.scheduleCollection);
        const scheduleUnsubscribe = onSnapshot(scheduleRef, (doc) => {
          if (doc.exists()) {
            const data = doc.data();
            const scheduleData = data.schedule || [];

            // Extract league name
            let leagueName = '';
            if (scheduleData.length > 0 && scheduleData[0].matches && scheduleData[0].matches.length > 0) {
              leagueName = scheduleData[0].matches[0].leagueName || '';
            }

            setAllTeamData(prev => ({
              ...prev,
              [team.id]: {
                ...prev[team.id],
                leagueName,
                scheduleData,
                loading: false
              }
            }));
          } else {
            setAllTeamData(prev => ({
              ...prev,
              [team.id]: {
                ...prev[team.id],
                error: 'Spielplan nicht gefunden',
                loading: false
              }
            }));
          }
        }, (error) => {
          setAllTeamData(prev => ({
            ...prev,
            [team.id]: {
              ...prev[team.id],
              error: 'Fehler beim Laden des Spielplans',
              loading: false
            }
          }));
        });

        // Standings data listener
        const standingsRef = doc(db, 'standings', team.standingCollection);
        const standingsUnsubscribe = onSnapshot(standingsRef, (doc) => {
          if (doc.exists()) {
            const data = doc.data();
            const standingsData = data.standings || [];

            setAllTeamData(prev => ({
              ...prev,
              [team.id]: {
                ...prev[team.id],
                standingsData
              }
            }));
          }
        }, (error) => {
          console.error('Error loading standings for team', team.id, error);
        });

        unsubscribers.push(scheduleUnsubscribe, standingsUnsubscribe);
      });

      // Mark preloading as done after a short delay
      setTimeout(() => {
        setIsPreloading(false);
      }, 2000);
    };

    preloadAllTeams();

    // Cleanup function
    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, []);

  return { allTeamData, isPreloading };
};